/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import last from 'lodash/last';
import isEqual from 'lodash/isEqual';

import {
    Row,
    Cell,
    Button,
    TextField,
    Icon,
    FlexRow,
    withLDConsumer,
    Alert,
} from 'sarsaparilla';

import { OrderDetailsSection } from 'shared-ui/components/OrderDetailsLayout/OrderDetailsSection';
import { today } from '@internationalized/date';
import {
    FacilityDefaults,
    FacilityProps,
    PassInfoProps,
    PassTypeDefaults,
    PassTypeProps,
} from '../props';
import { INTERAGENCY_DISCOUNT_PASSES } from '../constants';
import { passSelectionRouteWithReservation } from '../../dev/routesArray';
import { validToDate } from '../utils/duration_utils';
import SelectInput from './inputs/SelectInput';
import TextInput from './inputs/TextInput';
import StartDateForm from './StartDateForm';
import HolderInfoForm from './HolderInfoForm';
import VehicleInfoForm from './VehicleInfoForm';
import WatercraftInfoForm from './WatercraftInfoForm';
import AttendeeInfoForm from './AttendeeInfoForm';
import OptionSelectForm from './OptionSelectForm';
import FlexTextAttributeForm from './FlexTextAttributeForm';
import MailingAddressForm from './MailingAddressForm';
import { getSeasonDates, isPassNumberValid } from '../utils';
import { parseDate } from '../utils/date_time_utils';

export class PassInfoForm extends Component {
    static defaultProps = {
        number: 1,
        onChange: () => {},
        onRemove: () => {},
        submitAttempted: false,
        facility: FacilityDefaults,
        passType: PassTypeDefaults,
    };

    static propTypes = {
        disabled: PropTypes.bool,
        facility: FacilityProps,
        isInternal: PropTypes.bool,
        isLegacy: PropTypes.bool,
        isPending: PropTypes.bool,
        isScannedPass: PropTypes.bool,
        number: PropTypes.number,
        onChange: PropTypes.func,
        onRemove: PropTypes.func,
        passInfo: PassInfoProps,
        passType: PassTypeProps,
        renderModify: PropTypes.bool,
        renderRemove: PropTypes.bool,
        submitAttempted: PropTypes.bool,
        reservationId: PropTypes.string,
        reset: PropTypes.bool,
        isFieldSales: PropTypes.bool,
        skipCustomerInfo: PropTypes.bool,
        verifyAddress: PropTypes.func,
        flags: PropTypes.object,
    };

    state = {
        attendeeInfoIsValid: true,
        endDate: undefined,
        endDateIsValid: true,
        features: {},
        holderInfoIsValid: true,
        isAddingDiscount: !!get(
            this.props.passInfo,
            'features.discount.pass_number',
            false
        ),
        passInfo: this.props.passInfo,
        startDateIsValid: true,
        vehicleInfoIsValid: true,
        watercraftInfoIsValid: true,
        optionSelectIsValid: true,
        flexTextAttributeIsValid: true,
        mailingInfoIsValid: true,
        isRemoving: false,
    };

    passInfoFormRef = null;

    componentDidMount() {
        if (this.props.isInternal && this.passInfoFormRef) {
            this.passInfoFormRef
                .querySelectorAll(
                    'input, textarea, button, select, .rec-select, .rec-dropdown'
                )
                // eslint-disable-next-line no-param-reassign, no-return-assign
                .forEach((el) => (el.tabIndex = -1));
        }
        const { passInfo } = this.state;
        if (passInfo && !get(passInfo.features, ['discount', 'pass_type'], '')) {
            this.handleInteragencyTypeChange(INTERAGENCY_DISCOUNT_PASSES[0]);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (!isEqual(this.state.passInfo, prevState.passInfo)) {
            this.props.onChange(
                this.props.passType.id,
                this.state.passInfo,
                this.formIsValid()
            );
        }

        const { reset, passInfo } = this.props;
        if (reset) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState(() => ({ passInfo }));
        }
    }

    onRemove = () => {
        this.setState(
            {
                isRemoving: true,
            },
            () => {
                const { reservationId } = this.props;
                this.props.onRemove(reservationId);
            }
        );
    };

    onModify = () => {
        const { passType, reservationId } = this.props;
        window.location.assign(
            passSelectionRouteWithReservation(passType.facility_id, reservationId)
        );
    };

    onStartDateChanged = (startDate, startDateIsValid) => {
        this.setState((prevState) => ({
            ...prevState,
            passInfo: {
                ...prevState.passInfo,
                start_date: startDate,
                features: {
                    ...prevState.passInfo.features,
                    'start-date': startDate,
                },
            },
            startDateIsValid,
        }));
    };

    onEndDateChanged = (endDate, endDateIsValid) => {
        const { passType } = this.props;
        const duration = this.props?.passInfo?.duration;
        let endValue = validToDate(this.props?.passInfo?.start_date, passType);
        const [, seasonEnd] = getSeasonDates(
            this.props?.passType?.periods,
            undefined,
            this.props?.flags?.useApDateFix
        );
        const [year, month, day] = seasonEnd?.split('-') ?? [
            undefined,
            undefined,
            undefined,
        ];
        const adjustedYear = parseInt(year, 10) + 1;

        if (!endValue) {
            endValue = parseDate(`${adjustedYear}-${month}-${day}`);
        }

        const endDateValue =
            duration === 'season' || duration === 'seasonal'
                ? endValue.toString() // Made this change because the end_date is expected as string in PassSelection components, is it stored as a string in the database (??)
                : endDate;
        this.setState((prevState) => ({
            ...prevState,
            endDate: endDateValue,
            passInfo: {
                ...prevState.passInfo,
                end_date: endDateValue,
                features: {
                    ...prevState.passInfo.features,
                    'end-date': endDateValue,
                },
            },
            endDateIsValid,
        }));
    };

    onDaySegmentsChanged = (daySegmentInfo) => {
        this.setState((prevState) => ({
            ...prevState,
            passInfo: {
                ...prevState.passInfo,
                features: {
                    ...prevState.passInfo.features,
                    'day-segment': {
                        ...prevState.passInfo.features['day-segment'],
                        segments: daySegmentInfo,
                    },
                },
            },
        }));
    };

    onVehicleInfoChanged = (vehicleInfo, vehicleInfoIsValid) => {
        this.setState((prevState) => ({
            ...prevState,
            passInfo: {
                ...prevState.passInfo,
                vehicle_info: vehicleInfo,

                features: {
                    ...prevState.passInfo.features,
                    'equipment-info': {
                        ...prevState.passInfo.features['equipment-info'],
                        vehicle: vehicleInfo,
                    },
                },
            },
            vehicleInfoIsValid,
        }));
    };

    onWatercraftInfoChanged = (watercraftInfo, watercraftInfoIsValid) => {
        this.setState((prevState) => ({
            ...prevState,
            passInfo: {
                ...prevState.passInfo,
                watercraft_info: watercraftInfo,

                features: {
                    ...prevState.passInfo.features,
                    'equipment-info': {
                        ...prevState.passInfo.features['equipment-info'],
                        watercraft: watercraftInfo,
                    },
                },
            },
            watercraftInfoIsValid,
        }));
    };

    onOptionSelectChange = (selection, optionSelectIsValid) => {
        const { type } = selection;
        this.setState((prevState) => {
            let optSelection = get(prevState, ['passInfo', 'features', 'option-select']);
            if (!optSelection) {
                optSelection = [];
            }
            return {
                ...prevState,
                passInfo: {
                    ...prevState.passInfo,
                    features: {
                        ...prevState.passInfo.features,
                        'option-select': optSelection
                            .filter((opt) => opt.type !== type)
                            .concat([selection]),
                    },
                },
                optionSelectIsValid,
            };
        });
    };

    onFlexTextAttributeChange = (inputText, flexTextAttributeIsValid) => {
        const { passType } = this.props;
        const label = get(passType, ['features', 'flex-text-attr', 'label']);

        this.setState((prevState) => {
            return {
                ...prevState,
                passInfo: {
                    ...prevState.passInfo,
                    features: {
                        ...prevState.passInfo.features,
                        'flex-text-attr': {
                            ...prevState.passInfo.features['flex-text-attr'],
                            label,
                            input_text: inputText,
                        },
                    },
                },
                flexTextAttributeIsValid,
            };
        });
    };

    onHolderInfoChanged = (holderInfo, holderInfoIsValid) => {
        this.setState((prevState) => ({
            ...prevState,
            passInfo: { ...prevState.passInfo, holder_info: holderInfo },
            holderInfoIsValid,
        }));
    };

    onAttendeeInfoChanged = (attendeeInfo, attendeeInfoIsValid) => {
        const daySegment = this.props.passType.features['day-segment']
            ? {
                  'day-segment': {
                      ...this.state.passInfo.features['day-segment'],
                      groups: attendeeInfo,
                  },
              }
            : {};
        this.setState((prevState) => ({
            ...prevState,
            passInfo: {
                ...prevState.passInfo,
                attendee_info: attendeeInfo,

                features: {
                    ...prevState.passInfo.features,
                    'attendee-info': attendeeInfo,
                    ...daySegment,
                },
            },
            attendeeInfoIsValid,
        }));
    };

    onMailingAddressChange = (key, value, mailingInfoIsValid) => {
        const keyValue = get(this.state.passInfo, `features['mailing-info'][${key}]`, '');
        if (keyValue === value) {
            return;
        }

        this.setState((prevState) => {
            return {
                ...prevState,
                passInfo: {
                    ...prevState.passInfo,
                    holder_info:
                        key === 'zip_code'
                            ? {
                                  ...prevState.passInfo.holder_info,
                                  [key]: value,
                              }
                            : {
                                  ...prevState.passInfo.holder_info,
                              },
                    features: {
                        ...prevState.passInfo.features,
                        'mailing-info': {
                            ...prevState.passInfo.features['mailing-info'],
                            [key]: value,
                        },
                    },
                },
                mailingInfoIsValid,
            };
        });
    };

    handleDiscountChange = (discount) => {
        this.setState((prevState) => ({
            ...prevState,
            passInfo: {
                ...prevState.passInfo,
                features: {
                    ...prevState.passInfo.features,
                    discount: {
                        ...prevState.passInfo.features.discount,
                        pass_number: discount,
                    },
                },
            },
        }));
    };

    handleInteragencyTypeChange = (passType) => {
        this.setState((prevState) => ({
            ...prevState,
            passInfo: {
                ...prevState.passInfo,
                features: {
                    ...prevState.passInfo.features,
                    discount: {
                        ...prevState.passInfo.features.discount,
                        pass_type: passType,
                    },
                },
            },
        }));
    };

    renderAttendeeInfo = () => {
        const {
            facility,
            isInternal,
            passType,
            number,
            submitAttempted,
            isPending,
            reservationId,
            isFieldSales,
            skipCustomerInfo,
        } = this.props;
        const { passInfo, endDate } = this.state;

        const lastSegmentDate = last(
            Object.keys(get(passInfo, ['features', 'day-segment', 'segments'], {}))
        );
        const endDateFromSegments = `${today().year}-${lastSegmentDate}`;

        return (
            <div>
                {isInternal && <h3>Pass Holder Details</h3>}

                <HolderInfoForm
                    number={number}
                    isInternal={isInternal}
                    submitAttempted={submitAttempted}
                    holderInfo={passInfo?.holder_info}
                    passType={passType}
                    onChange={this.onHolderInfoChanged}
                    isFieldSales={isFieldSales}
                    skipCustomerInfo={skipCustomerInfo}
                />

                {isInternal && (
                    <div>
                        <h3>Order Details</h3>
                        <Row>
                            <Cell width={6}>
                                <TextInput
                                    id={`facility_name_${number}`}
                                    value={passInfo.facility_name}
                                    label="Facility Name"
                                />
                            </Cell>
                            <Cell width={6}>
                                <TextInput
                                    id={`pass_name_${number}`}
                                    value={passInfo.pass_name}
                                    label="Pass Name"
                                />
                            </Cell>
                        </Row>
                    </div>
                )}

                <StartDateForm
                    isInternal={isInternal}
                    startDateDisabled={false}
                    number={number}
                    isExistingReservation={!!reservationId && !isPending}
                    startDate={passInfo?.features['start-date']}
                    endDate={endDate || endDateFromSegments}
                    segments={get(passInfo, ['features', 'day-segment', 'segments'])}
                    passType={passType}
                    onChangeStart={this.onStartDateChanged}
                    onChangeEnd={this.onEndDateChanged}
                    onChangeDaySegments={this.onDaySegmentsChanged}
                    facility={facility}
                    updateAddToCartState={this.props.updateAddToCartState}
                />

                {}
            </div>
        );
    };

    renderFeatureAttendeeInfo = () => {
        const { passType, number, skipCustomerInfo } = this.props;
        const { passInfo } = this.state;

        return (
            <AttendeeInfoForm
                number={number}
                skipCustomerInfo={skipCustomerInfo}
                groups={
                    passType?.features['attendee-info'] ||
                    get(passType, ['features', 'day-segment', 'groups'])
                }
                attendeeInfo={
                    passInfo?.features['attendee-info'] ||
                    get(passInfo, ['features', 'day-segment', 'groups'])
                }
                passType={passType}
                onChange={this.onAttendeeInfoChanged}
            />
        );
    };

    renderVehicleInfo = () => {
        const { isScannedPass, passType, submitAttempted, number, skipCustomerInfo } =
            this.props;

        if (!get(passType, ['features', 'equipment-info', 'vehicle'])) {
            return null;
        }

        return (
            <VehicleInfoForm
                isScannedPass={isScannedPass}
                number={number}
                submitAttempted={submitAttempted}
                passType={passType}
                onChange={this.onVehicleInfoChanged}
                skipCustomerInfo={skipCustomerInfo}
            />
        );
    };

    renderWatercraftInfo = () => {
        const {
            isScannedPass,
            passType,
            submitAttempted,
            number,
            reservationId,
            skipCustomerInfo,
        } = this.props;
        const { passInfo } = this.state;

        if (!get(passType, ['features', 'equipment-info', 'watercraft'])) {
            return null;
        }

        return (
            <WatercraftInfoForm
                isScannedPass={isScannedPass}
                number={number}
                submitAttempted={submitAttempted}
                reservationId={reservationId}
                watercraftInfo={passInfo?.features['equipment-info'].watercraft}
                passType={passType}
                onChange={this.onWatercraftInfoChanged}
                skipCustomerInfo={skipCustomerInfo}
            />
        );
    };

    renderFeatureFields = () => {
        const { isScannedPass } = this.props;
        const optionSelectInfo = this.renderOptionSelectInfo();
        const flexTextAttributeInfo = this.renderFlexTextAttributeInfo();

        if (!optionSelectInfo && !flexTextAttributeInfo) {
            return null;
        }

        return (
            <FlexRow className={isScannedPass ? 'scanned-pass' : ''}>
                {optionSelectInfo}
                {flexTextAttributeInfo}
            </FlexRow>
        );
    };

    renderMailingAddress = () => {
        const { passType, number, submitAttempted, skipCustomerInfo } = this.props;
        const { passInfo } = this.state;
        const mailingInfo = get(passInfo, ['features', 'mailing-info']) || {};

        if (!passType?.features?.['mailing-info']) {
            return null;
        }

        return (
            <MailingAddressForm
                mailingInfo={mailingInfo}
                number={number}
                skipCustomerInfo={skipCustomerInfo}
                onChange={this.onMailingAddressChange}
                submitAttempted={submitAttempted}
                verifyAddress={this.props.verifyAddress}
            />
        );
    };

    renderOptionSelectInfo = () => {
        const { passType, submitAttempted, number, skipCustomerInfo } = this.props;
        const { passInfo } = this.state;

        const options = get(passType, ['features', 'option-select']);
        if (!options) {
            return null;
        }

        return (
            <OptionSelectForm
                options={options}
                selectedOptions={passInfo.features['option-select']}
                reservationPosition={number}
                skipCustomerInfo={skipCustomerInfo}
                submitAttempted={submitAttempted}
                onChange={this.onOptionSelectChange}
            />
        );
    };

    renderFlexTextAttributeInfo = () => {
        const { passType, submitAttempted, number, skipCustomerInfo } = this.props;
        const { passInfo } = this.state;
        const label = get(passType, ['features', 'flex-text-attr', 'label']);
        const value = get(passInfo, ['features', 'flex-text-attr', 'input_text']) || '';

        if (!label) {
            return null;
        }

        return (
            <FlexTextAttributeForm
                number={number}
                label={label}
                value={value}
                skipCustomerInfo={skipCustomerInfo}
                reservationPosition={number}
                submitAttempted={submitAttempted}
                onChange={this.onFlexTextAttributeChange}
            />
        );
    };

    validatePassNumber = (value) => {
        const { passInfo } = this.state;
        const interagencyPassType = get(
            passInfo?.features,
            ['discount', 'pass_type'],
            ''
        );

        return {
            isValid: isPassNumberValid(interagencyPassType, value),
            formattedValue: null,
            errorText: 'Pass ID is invalid.',
        };
    };

    formIsValid() {
        const {
            endDateIsValid,
            startDateIsValid,
            vehicleInfoIsValid,
            watercraftInfoIsValid,
            optionSelectIsValid,
            flexTextAttributeIsValid,
            holderInfoIsValid,
            attendeeInfoIsValid,
            mailingInfoIsValid,
        } = this.state;
        const {
            passType: { features },
        } = this.props;
        let isValid = true;

        if (features['end-date']) {
            isValid = isValid && endDateIsValid;
        }
        if (features['start-date']) {
            isValid = isValid && startDateIsValid;
        }
        if (get(features, ['equipment-info', 'vehicle'])) {
            isValid = isValid && vehicleInfoIsValid;
        }
        if (get(features, ['equipment-info', 'watercraft'])) {
            isValid = isValid && watercraftInfoIsValid;
        }
        if (get(features, ['option-select'])) {
            isValid = isValid && optionSelectIsValid;
        }
        if (get(features, ['flex-text-attr'])) {
            isValid = isValid && flexTextAttributeIsValid;
        }
        if (features['attendee-info']) {
            isValid = isValid && attendeeInfoIsValid;
        }
        if (get(features, ['mailing-info'])) {
            isValid = isValid && mailingInfoIsValid;
        }

        isValid = isValid && holderInfoIsValid;

        return isValid;
    }

    renderDiscount = () => {
        const { isInternal, number, submitAttempted, passType } = this.props;
        const { passInfo, isAddingDiscount } = this.state;
        const isPassTypeRequired = !!get(
            passInfo?.features,
            ['discount', 'pass_number'],
            ''
        );

        if (!get(passType, ['features', 'discount']) || isInternal) {
            return null;
        }

        return (
            <Row>
                <Cell width={6}>
                    {isAddingDiscount ? (
                        <div>
                            <SelectInput
                                id={`discount-pass-type-${number}`}
                                label="Pass Type"
                                value={get(
                                    passInfo.features,
                                    ['discount', 'pass_type'],
                                    ''
                                )}
                                options={INTERAGENCY_DISCOUNT_PASSES.map((label) => ({
                                    label,
                                    value: label,
                                }))}
                                onChange={this.handleInteragencyTypeChange}
                                showError
                                required={isPassTypeRequired}
                                isValid={
                                    submitAttempted && isPassTypeRequired
                                        ? !!get(
                                              passInfo.features,
                                              ['discount', 'pass_type'],
                                              ''
                                          )
                                        : true
                                }
                            />
                        </div>
                    ) : (
                        <Button
                            appearance="secondary"
                            onClick={() => this.setState({ isAddingDiscount: true })}
                            iconBeforeElement={<Icon iconName="cart-add" />}
                        >
                            Add a Pass Discount
                        </Button>
                    )}
                </Cell>
                {isAddingDiscount && (
                    <Cell width={6}>
                        <div>
                            <TextField
                                id={`discount-pass-number-${number}`}
                                label="Pass ID"
                                shouldValidateOnUpdate={submitAttempted}
                                value={get(
                                    passInfo.features,
                                    ['discount', 'pass_number'],
                                    ''
                                )}
                                onBlur={(e) => this.handleDiscountChange(e.target.value)}
                                validator={this.validatePassNumber}
                            />
                        </div>
                    </Cell>
                )}
            </Row>
        );
    };

    renderPassHolderName() {
        const { passInfo } = this.state;
        const holderInfoFilledOut =
            passInfo?.holder_info && passInfo.holder_info?.first_name;

        if (!holderInfoFilledOut) {
            return null;
        }

        return (
            <span className="activitypass-pass-info-form-heading-passholder">{`: ${passInfo?.holder_info?.first_name} ${passInfo?.holder_info?.last_name}`}</span>
        );
    }

    render() {
        const {
            disabled,
            isInternal,
            isLegacy,
            isPending,
            number,
            passType,
            renderModify,
            renderRemove,
            reservationId,
        } = this.props;
        const isExistingReservation = !!reservationId && !isPending;

        return (
            <div
                id={`activitypass-pass-info-form-${number}`}
                className={`activitypass-pass-info-form ${
                    disabled ? 'activitypass-pass-info-form--disabled' : ''
                }`}
                ref={(ref) => {
                    this.passInfoFormRef = ref;
                    return null;
                }}
            >
                <OrderDetailsSection
                    heading={
                        !isInternal && (
                            <span className="activitypass-pass-info-form-heading">
                                <span>{number}.&nbsp;</span>
                                {passType.name}
                                {this.renderPassHolderName()}
                            </span>
                        )
                    }
                    onModifyClick={!isInternal && renderModify && this.onModify}
                    onRemoveClick={!isInternal && renderRemove && this.onRemove}
                    useRemoveConfirmationModal={isExistingReservation}
                >
                    <div className="activitypass-pass-info-form-form" aria-live="polite">
                        {isLegacy && (
                            <Alert type="warning" hasAriaLive>
                                <p>
                                    This pass was purchased through YourPassNow before
                                    Activity Passes were offered on{' '}
                                    {process.env.SITE_NAME}. A refund cannot be issued for
                                    this pass as it is a legacy purchase prior to
                                    integration with
                                    {process.env.SITE_NAME}.
                                </p>
                            </Alert>
                        )}

                        {this.renderAttendeeInfo()}

                        {this.renderVehicleInfo()}

                        {this.renderWatercraftInfo()}

                        {this.renderFeatureAttendeeInfo()}

                        {this.renderFeatureFields()}

                        {this.renderMailingAddress()}

                        {this.renderDiscount()}
                    </div>
                </OrderDetailsSection>
            </div>
        );
    }
}

// cspell:ignore activitypass, passholder
export default withLDConsumer()(PassInfoForm);
